/**
 * useFilters
 * ──────────
 * Everything related to narrowing what's visible on the map:
 *   - Text search (with debounced output)
 *   - Zone filtering
 *   - Quick filters (starred, mine, has challenges, has forks, my cohorts)
 *   - Type filters (physical / digital / hybrid)
 *   - Sort order
 *   - Challenge visibility toggle
 *
 * v2 CHANGE: Starred state moved to TrayContext (global).
 * This hook now receives `starredIds` as a parameter for filtering,
 * but does not own or mutate it.
 *
 * v3 CHANGE: Added 'my_cohorts' quick filter. Requires the map data's
 * projects to carry a `cohort_ids` array (added server-side in
 * map_api.py's project_to_map_dict) and a `userCohortIds` array — the
 * IDs of cohorts the current user belongs to (fetched via GET /api/cohorts
 * the same way MyCohortsPage.jsx does). Pass userCohortIds in as a
 * parameter, same pattern as starredIds — this hook doesn't own or
 * fetch that list itself.
 *
 * Place at: src/hooks/useFilters.js
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { ZONES } from '../config/zones';

// ────────────────────────────────────────────────────────────────────────────
// Debounce utility (inline to avoid extra dependency)
// ────────────────────────────────────────────────────────────────────────────

function useDebounceValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ────────────────────────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────────────────────────

export default function useFilters({
  isAuthenticated, userId, mapData, starredIds = [], userCohortIds = [],
}) {
  // Text search
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounceValue(searchQuery, 300);

  // Zone filter (empty = all zones visible)
  const [activeZones, setActiveZones] = useState([]);

  // Quick filters
  const [activeQuickFilters, setActiveQuickFilters] = useState([]);

  // Project type filters
  const [activeTypeFilters, setActiveTypeFilters] = useState([]);

  // Shape tag filters (sensory — round, wide, narrow, etc.)
  const [activeShapeFilters, setActiveShapeFilters] = useState([]);

  // Sort
  const [sortBy, setSortBy] = useState('newest');

  // Challenge visibility
  const [showChallenges, setShowChallenges] = useState(true);

  // ── Toggle helpers ──────────────────────────────────────────────────────

  const toggleZone = useCallback((zoneId) => {
    setActiveZones(prev =>
      prev.includes(zoneId) ? prev.filter(z => z !== zoneId) : [...prev, zoneId]
    );
  }, []);

  const toggleQuickFilter = useCallback((filterId) => {
    setActiveQuickFilters(prev =>
      prev.includes(filterId) ? prev.filter(f => f !== filterId) : [...prev, filterId]
    );
  }, []);

  const toggleTypeFilter = useCallback((typeId) => {
    setActiveTypeFilters(prev =>
      prev.includes(typeId) ? prev.filter(t => t !== typeId) : [...prev, typeId]
    );
  }, []);

  const toggleShapeFilter = useCallback((shapeId) => {
    setActiveShapeFilters(prev =>
      prev.includes(shapeId) ? prev.filter(s => s !== shapeId) : [...prev, shapeId]
    );
  }, []);

  // ── Filtered + sorted data (the big memo) ─────────────────────────────

  const filteredData = useMemo(() => {
    let projects = mapData.projects || [];
    let challenges = mapData.challenges || [];
    let connections = mapData.connections || [];

    // Text search
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      projects = projects.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        (p.tags || []).some(t =>
          (typeof t === 'string' ? t : t.name || '').toLowerCase().includes(q)
        ) ||
        (p.author?.username || '').toLowerCase().includes(q)
      );
      challenges = challenges.filter(c =>
        (c.challenge_title || c.title || '').toLowerCase().includes(q) ||
        (c.challenge_description || c.description || '').toLowerCase().includes(q)
      );
    }

    // Zone filters
    if (activeZones.length > 0) {
      projects = projects.filter(p => {
        const tags = (p.tags || []).map(t => typeof t === 'string' ? t : t.name || '').join(' ').toLowerCase();
        const allText = `${tags} ${(p.name || '').toLowerCase()} ${(p.description || '').toLowerCase()}`;
        return activeZones.some(zoneId => {
          const zone = ZONES[zoneId];
          return zone?.keywords.some(k => allText.includes(k.toLowerCase()));
        });
      });
    }

    // Type filters
    if (activeTypeFilters.length > 0) {
      projects = projects.filter(p => {
        const pType = p.project_type || p.projectType || 'digital';
        return activeTypeFilters.includes(pType);
      });
    }

    // Shape tag filters — project must have at least one matching shape
    if (activeShapeFilters.length > 0) {
      projects = projects.filter(p => {
        const raw = p.shape_tags || p.shapeTags || '';
        const projectShapes = Array.isArray(raw)
          ? raw.map(s => String(s).trim()).filter(Boolean)
          : String(raw).split(',').map(s => s.trim()).filter(Boolean);
        return activeShapeFilters.some(shape => projectShapes.includes(shape));
      });
    }

    // Quick filters
    if (activeQuickFilters.includes('starred')) {
      projects = projects.filter(p => starredIds.includes(p.id));
    }
    if (activeQuickFilters.includes('mine')) {
      projects = projects.filter(p =>
        (p.user_id || p.userId) === userId || p.author?.id === userId
      );
    }
    if (activeQuickFilters.includes('challenges')) {
      const withChallenges = new Set(
        challenges.map(c => c.project_id || c.projectId).filter(Boolean)
      );
      projects = projects.filter(p => withChallenges.has(p.id));
    }
    if (activeQuickFilters.includes('has_forks')) {
      projects = projects.filter(p => (p.fork_count || p.forkCount || 0) > 0);
    }
    // "My Cohorts" — projects belonging to any cohort the current user is
    // a member of. Relies on map_api.py's project_to_map_dict() including
    // cohort_ids on every project (a batched lookup, not per-project — see
    // that file's comments). If userCohortIds is empty (user isn't in any
    // cohort), this filter simply matches nothing, same as any other quick
    // filter would when its underlying data is empty — no special casing
    // needed here.
    if (activeQuickFilters.includes('my_cohorts')) {
      const cohortIdSet = new Set(userCohortIds);
      projects = projects.filter(p =>
        (p.cohort_ids || p.cohortIds || []).some(cid => cohortIdSet.has(cid))
      );
    }

    // Sorting
    const sorted = [...projects];
    switch (sortBy) {
      case 'most_forked':
        sorted.sort((a, b) => (b.fork_count || 0) - (a.fork_count || 0));
        break;
      case 'most_viewed':
        sorted.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
        break;
      case 'most_starred':
        sorted.sort((a, b) => (b.star_count || 0) - (a.star_count || 0));
        break;
      default: {
        // newest
        sorted.sort((a, b) => {
          const aD = a.created_at || a.createdAt || '';
          const bD = b.created_at || b.createdAt || '';
          return bD.localeCompare(aD);
        });
      }
    }
    projects = sorted;

    // Prune connections to only visible projects
    const visible = new Set(projects.map(p => p.id));
    connections = connections.filter(c => {
      if (c.sourceType === 'project' && !visible.has(c.sourceId)) return false;
      if (c.targetType === 'project' && !visible.has(c.targetId)) return false;
      return true;
    });

    return {
      projects,
      challenges: showChallenges ? challenges : [],
      connections,
    };
  }, [
    mapData, debouncedSearch, activeZones, activeTypeFilters, activeShapeFilters,
    showChallenges, activeQuickFilters, sortBy, starredIds, userId, userCohortIds,
  ]);

  // ── Public API ─────────────────────────────────────────────────────────

  return {
    // State
    searchQuery,
    activeZones,
    activeQuickFilters,
    activeTypeFilters,
    activeShapeFilters,
    sortBy,
    showChallenges,

    // Setters
    setSearchQuery,
    setSortBy,
    setShowChallenges,

    // Toggles
    toggleZone,
    toggleQuickFilter,
    toggleTypeFilter,
    toggleShapeFilter,

    // Computed
    filteredData,
  };
}